import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Layout, Typography, Card, Button, Row, Col, Divider, 
  Tag, Space, Spin, Badge, Empty, Modal, List, Avatar, Skeleton 
} from 'antd';
import { 
  CheckOutlined, CrownOutlined, RocketOutlined, 
  MailOutlined, PhoneOutlined, MessageOutlined, UserOutlined 
} from '@ant-design/icons';
import axiosInstance from '../../axiosInstance';
import { BACKENDURL } from '../../components/constants';

const { Title, Text, Paragraph } = Typography;
const { Content } = Layout;

const includedFeatures = [
  'Real-time sales tracking',
  'Inventory management',
  'Multi-user access',
  'Data security & backups',
  'Cloud synchronization'
];

const salesContacts = [
  {
    icon: <MailOutlined />,
    title: 'Email Support',
    description: 'Get quick responses via email',
    details: 'support@sellsmart.co.tz',
    action: 'mailto:support@sellsmart.co.tz'
  },
  {
    icon: <PhoneOutlined />,
    title: 'Phone Support',
    description: 'Speak directly with our team',
    details: '+255 782 913 983',
    action: 'tel:+255782913983'
  },
  {
    icon: <MessageOutlined />,
    title: 'WhatsApp Business',
    description: 'Chat with us on WhatsApp',
    details: '+255 782 913 983',
    action: 'https://wa.me/255782913983'
  },
  {
    icon: <UserOutlined />,
    title: 'Sales Representative',
    description: 'Dedicated account manager',
    details: 'Mugabo Manyama',
    action: 'mailto:manyama@sellsmart.co.tz'
  }
];

const PricingPage = () => {
  const [pricingPlans, setPricingPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [contactModalVisible, setContactModalVisible] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchPricingPlans();
  }, []);

  const fetchPricingPlans = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`${BACKENDURL}/packages`);
      setPricingPlans(response.data.packages || []);
    } catch (err) {
      setError('Failed to load pricing plans');
      console.error('Error fetching pricing plans:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleGetStarted = () => {
    navigate('/in-touch/register');
  };

  const handleContactSales = () => {
    setContactModalVisible(true);
  };

  const handleContactAction = (action) => {
    if (action.startsWith('http') || action.startsWith('mailto') || action.startsWith('tel')) {
      window.open(action, '_blank');
    }
  };

  const getPlanColor = (isPopular) => {
    return isPopular ? '#ff4f2e' : '#0d306d';
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-TZ', {
      style: 'currency',
      currency: 'TZS',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  // Skeleton loader for pricing cards
  const renderSkeletonCards = () => {
    return [1, 2, 3].map((item) => (
      <Col key={item} xs={24} md={12} lg={8}>
        <Card 
          style={{ 
            borderRadius: '16px',
            border: '2px solid #f0f0f0',
            height: '100%',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
          }}
          bodyStyle={{ padding: '32px 24px' }}
        >
          <Skeleton active paragraph={{ rows: 8 }} />
        </Card>
      </Col>
    ));
  };

  if (error) {
    return (
      <Layout style={{ background: '#ffffff', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Content style={{ textAlign: 'center', padding: '20px' }}>
          <Title level={3} style={{ color: '#ff4d4f', marginBottom: 16 }}>Error Loading Plans</Title>
          <Paragraph style={{ marginBottom: 24, color: '#666', maxWidth: '400px' }}>{error}</Paragraph>
          <Button type="primary" onClick={fetchPricingPlans} size="large">
            Try Again
          </Button>
        </Content>
      </Layout>
    );
  }

  // Empty state when no packages are available
  if (!loading && pricingPlans.length === 0) {
    return (
      <Layout style={{ background: '#ffffff', minHeight: '100vh' }}>
        <Content style={{ padding: '0 24px' }}>
          {/* Hero Section */}
          <section style={{ textAlign: 'center', padding: '80px 0 40px' }}>
            <Title level={1} style={{ fontSize: '3rem', color: '#0d306d', marginBottom: 16, fontWeight: 700 }}>
              Simple, Transparent Pricing
            </Title>
            <Paragraph style={{ fontSize: '1.2rem', maxWidth: '800px', margin: '0 auto', color: '#666' }}>
              Choose the perfect plan for your business needs. All plans include a 14-day free trial.
            </Paragraph>
          </section>

          {/* Empty State */}
          <section style={{ padding: '60px 0', textAlign: 'center' }}>
            <Empty
              image={<RocketOutlined style={{ fontSize: '64px', color: '#0d306d' }} />}
              description={
                <div>
                  <Title level={4} style={{ color: '#0d306d', marginBottom: 8 }}>
                    No Packages Available
                  </Title>
                  <Paragraph style={{ color: '#666', maxWidth: '500px', margin: '0 auto' }}>
                    Currently, there are no pricing plans available. Please check back later or contact our sales team for more information.
                  </Paragraph>
                </div>
              }
            >
              <Button 
                type="primary" 
                size="large"
                onClick={handleContactSales}
                style={{ 
                  background: '#ff4f2e',
                  borderColor: '#ff4f2e',
                  fontWeight: '600',
                  padding: '0 32px',
                  height: '48px',
                  marginRight: '16px'
                }}
              >
                Contact Sales
              </Button>
              <Button 
                size="large"
                onClick={() => navigate('/in-touch/register')}
                style={{ 
                  color: '#0d306d',
                  borderColor: '#0d306d',
                  fontWeight: '600',
                  padding: '0 32px',
                  height: '48px'
                }}
              >
                Get Started
              </Button>
            </Empty>
          </section>

          {/* Features Included Section */}
          <section style={{ padding: '40px 0', textAlign: 'center', background: '#ffffff', borderRadius: '16px', marginBottom: '40px' }}>
            <Title level={2} style={{ color: '#0d306d', marginBottom: '16px', fontSize: '2rem' }}>
              All Plans Include
            </Title>
            <Paragraph style={{ maxWidth: '800px', margin: '0 auto 40px', color: '#666', fontSize: '1.1rem' }}>
              Every SellSmart subscription comes with these powerful features
            </Paragraph>
            
            <Row gutter={[24, 24]} style={{ maxWidth: '900px', margin: '0 auto' }}>
              {includedFeatures.map((feature, index) => (
                <Col key={index} xs={24} sm={12} md={8}>
                  <Card 
                    style={{ 
                      borderRadius: '12px',
                      border: '1px solid #f0f0f0',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                      height: '100%'
                    }}
                    bodyStyle={{ padding: '20px' }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <div style={{
                        background: '#ff4f2e',
                        borderRadius: '50%',
                        width: '32px',
                        height: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: '12px',
                        flexShrink: 0
                      }}>
                        <CheckOutlined style={{ color: 'white', fontSize: '16px' }} />
                      </div>
                      <Text strong style={{ fontSize: '14px' }}>{feature}</Text>
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>
          </section>
        </Content>

        {/* Contact Sales Modal */}
        <Modal
          title={
            <div style={{ textAlign: 'center' }}>
              <Title level={3} style={{ color: '#0d306d', margin: 0 }}>
                Contact Our Sales Team
              </Title>
              <Paragraph style={{ color: '#666', margin: 0 }}>
                We're here to help you choose the right solution
              </Paragraph>
            </div>
          }
          open={contactModalVisible}
          onCancel={() => setContactModalVisible(false)}
          footer={null}
          width={600}
          centered
        >
          <List
            itemLayout="horizontal"
            dataSource={salesContacts}
            renderItem={(item) => (
              <List.Item
                actions={[
                  <Button 
                    type="primary" 
                    size="small"
                    onClick={() => handleContactAction(item.action)}
                    style={{ background: '#ff4f2e', borderColor: '#ff4f2e' }}
                  >
                    Contact
                  </Button>
                ]}
                style={{ padding: '16px 0', border: 'none' }}
              >
                <List.Item.Meta
                  avatar={
                    <Avatar 
                      size="large" 
                      style={{ 
                        backgroundColor: '#0d306d',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      {item.icon}
                    </Avatar>
                  }
                  title={<Text strong>{item.title}</Text>}
                  description={
                    <div>
                      <div>{item.description}</div>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {item.details}
                      </Text>
                    </div>
                  }
                />
              </List.Item>
            )}
          />
          <Divider />
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <Text type="secondary">
              Business Hours: Monday - Saturday, 8:00 AM - 6:00 PM EAT
            </Text>
          </div>
        </Modal>
      </Layout>
    );
  }

  return (
    <Layout style={{ background: '#f8f9fa' }}>
      <Content style={{ padding: '0 24px' }}>
        {/* Hero Section */}
        <section style={{ textAlign: 'center', padding: '60px 0 40px' }}>
          <Title level={1} style={{ fontSize: '3rem', color: '#0d306d', marginBottom: 16, fontWeight: 700 }}>
            Simple, Transparent Pricing
          </Title>
          <Paragraph style={{ fontSize: '1.2rem', maxWidth: '800px', margin: '0 auto', color: '##666' }}>
            Choose the perfect plan for your business needs. All plans include a 14-day free trial.
          </Paragraph>
        </section>

        {/* Pricing Plans */}
        <section style={{ padding: '20px 0 60px' }}>
          <Row gutter={[32, 32]} justify="center">
            {loading ? (
              renderSkeletonCards()
            ) : (
              pricingPlans.map((plan) => {
                const planColor = getPlanColor(plan.is_popular);
                const isPopular = plan.is_popular;
                
                return (
                  <Col key={plan.id} xs={24} md={12} lg={8}>
                    <div style={{ position: 'relative', height: '100%' }}>
                      {isPopular && (
                        <Badge.Ribbon 
                          text="Most Popular" 
                          color={planColor}
                          style={{ 
                            fontSize: '12px',
                            fontWeight: '600',
                            padding: '0 12px',
                            height: '28px',
                            lineHeight: '28px'
                          }}
                        >
                          <Card 
                            style={{ 
                              borderRadius: '16px',
                              border: `2px solid ${planColor}`,
                              height: '100%',
                              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
                              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                              background: 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)'
                            }}
                            bodyStyle={{ 
                              padding: '32px 24px',
                              display: 'flex',
                              flexDirection: 'column',
                              height: '100%'
                            }}
                            hoverable
                            className="pricing-card"
                          >
                            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                              <Title 
                                level={3} 
                                style={{ 
                                  color: planColor, 
                                  marginBottom: '16px',
                                  fontSize: '1.5rem',
                                  fontWeight: 600
                                }}
                              >
                                {plan.name}
                              </Title>
                              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'baseline', marginBottom: '12px' }}>
                                <Title level={1} style={{ color: planColor, margin: '0 4px', fontSize: '3rem', fontWeight: 700 }}>
                                  {formatPrice(plan.price)}
                                </Title>
                                <Text style={{ color: '#8c8c8c', fontSize: '1rem' }}>/ month</Text>
                              </div>
                              <Text style={{ color: '#666', fontSize: '1rem', lineHeight: '1.4' }}>
                                {plan.description}
                              </Text>
                            </div>

                            <Divider style={{ margin: '20px 0', borderColor: '#f0f0f0' }} />

                            <div style={{ flex: 1, marginBottom: '24px' }}>
                              {/* Display shops count */}
                              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
                                <CheckOutlined style={{ color: planColor, marginRight: '12px', fontSize: '16px' }} />
                                <Text style={{ fontSize: '14px', fontWeight: 500 }}>
                                  {plan.shops_count === 0 ? 'Unlimited shops' : `Up to ${plan.shops_count} shops`}
                                </Text>
                              </div>

                              {/* Display warehouse count if applicable */}
                              {plan.count_warehouse > 0 && (
                                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
                                  <CheckOutlined style={{ color: planColor, marginRight: '12px', fontSize: '16px' }} />
                                  <Text style={{ fontSize: '14px', fontWeight: 500 }}>
                                    {plan.count_warehouse === 0 ? 'Unlimited warehouses' : `${plan.count_warehouse} warehouse(s)`}
                                  </Text>
                                </div>
                              )}

                              {/* Display features from the database */}
                              {plan.features && plan.features.map((feature, i) => (
                                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '12px' }}>
                                  <CheckOutlined style={{ color: planColor, marginRight: '12px', marginTop: '2px', fontSize: '14px' }} />
                                  <Text style={{ fontSize: '14px', lineHeight: '1.4' }}>{feature}</Text>
                                </div>
                              ))}

                              {/* Display trial information if available */}
                              {plan.has_trial && plan.trial_days && (
                                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px', marginTop: '16px' }}>
                                  <CrownOutlined style={{ color: '#faad14', marginRight: '12px', fontSize: '16px' }} />
                                  <Text strong style={{ color: '#faad14', fontSize: '14px' }}>
                                    {plan.trial_days}-day free trial included
                                  </Text>
                                </div>
                              )}
                            </div>

                            <div style={{ marginTop: 'auto' }}>
                              <Button 
                                type="primary" 
                                block 
                                size="large"
                                onClick={handleGetStarted}
                                style={{ 
                                  background: planColor,
                                  borderColor: planColor,
                                  fontWeight: '600',
                                  fontSize: '16px',
                                  height: '48px',
                                  borderRadius: '8px',
                                  boxShadow: '0 4px 12px rgba(13, 48, 109, 0.2)'
                                }}
                                className="pricing-button"
                              >
                                {plan.button_text}
                              </Button>
                            </div>
                          </Card>
                        </Badge.Ribbon>
                      )}
                      
                      {!isPopular && (
                        <Card 
                          style={{ 
                            borderRadius: '16px',
                            border: '2px solid #f0f0f0',
                            height: '100%',
                            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
                            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                            background: '#ffffff'
                          }}
                          bodyStyle={{ 
                            padding: '32px 24px',
                            display: 'flex',
                            flexDirection: 'column',
                            height: '100%'
                          }}
                          hoverable
                          className="pricing-card"
                        >
                          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                            <Title 
                              level={3} 
                              style={{ 
                                color: planColor, 
                                marginBottom: '16px',
                                fontSize: '1.5rem',
                                fontWeight: 600
                              }}
                            >
                              {plan.name}
                            </Title>
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'baseline', marginBottom: '12px' }}>
                              <Title level={1} style={{ color: planColor, margin: '0 4px', fontSize: '3rem', fontWeight: 700 }}>
                                {formatPrice(plan.price)}
                              </Title>
                              <Text style={{ color: '#8c8c8c', fontSize: '1rem' }}>/ month</Text>
                            </div>
                            <Text style={{ color: '#666', fontSize: '1rem', lineHeight: '1.4' }}>
                              {plan.description}
                            </Text>
                          </div>

                          <Divider style={{ margin: '20px 0', borderColor: '#f0f0f0' }} />

                          <div style={{ flex: 1, marginBottom: '24px' }}>
                            {/* Display shops count */}
                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
                              <CheckOutlined style={{ color: planColor, marginRight: '12px', fontSize: '16px' }} />
                              <Text style={{ fontSize: '14px', fontWeight: 500 }}>
                                {plan.shops_count === 0 ? 'Unlimited shops' : `Up to ${plan.shops_count} shops`}
                              </Text>
                            </div>

                            {/* Display warehouse count if applicable */}
                            {plan.count_warehouse > 0 && (
                              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
                                <CheckOutlined style={{ color: planColor, marginRight: '12px', fontSize: '16px' }} />
                                <Text style={{ fontSize: '14px', fontWeight: 500 }}>
                                  {plan.count_warehouse === 0 ? 'Unlimited warehouses' : `${plan.count_warehouse} warehouse(s)`}
                                </Text>
                              </div>
                            )}

                            {/* Display features from the database */}
                            {plan.features && plan.features.map((feature, i) => (
                              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '12px' }}>
                                <CheckOutlined style={{ color: planColor, marginRight: '12px', marginTop: '2px', fontSize: '14px' }} />
                                <Text style={{ fontSize: '14px', lineHeight: '1.4' }}>{feature}</Text>
                              </div>
                            ))}

                            {/* Display trial information if available */}
                            {plan.has_trial && plan.trial_days && (
                              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px', marginTop: '16px' }}>
                                <CrownOutlined style={{ color: '#faad14', marginRight: '12px', fontSize: '16px' }} />
                                <Text strong style={{ color: '#faad14', fontSize: '14px' }}>
                                  {plan.trial_days}-day free trial included
                                </Text>
                              </div>
                            )}
                          </div>

                          <div style={{ marginTop: 'auto' }}>
                            <Button 
                              type="default" 
                              block 
                              size="large"
                              onClick={handleGetStarted}
                              style={{ 
                                color: planColor,
                                borderColor: planColor,
                                fontWeight: '600',
                                fontSize: '16px',
                                height: '48px',
                                borderRadius: '8px',
                                background: 'transparent'
                              }}
                              className="pricing-button"
                            >
                              {plan.button_text}
                            </Button>
                          </div>
                        </Card>
                      )}
                    </div>
                  </Col>
                );
              })
            )}
          </Row>
        </section>

        {/* Features Included */}
        <section style={{ padding: '40px 0', textAlign: 'center', background: '#ffffff', borderRadius: '16px', marginBottom: '40px' }}>
          <Title level={2} style={{ color: '#0d306d', marginBottom: '16px', fontSize: '2rem' }}>
            All Plans Include
          </Title>
          <Paragraph style={{ maxWidth: '800px', margin: '0 auto 40px', color: '#666', fontSize: '1.1rem' }}>
            Every SellSmart subscription comes with these powerful features
          </Paragraph>
          
          <Row gutter={[24, 24]} style={{ maxWidth: '900px', margin: '0 auto' }}>
            {includedFeatures.map((feature, index) => (
              <Col key={index} xs={24} sm={12} md={8}>
                <Card 
                  style={{ 
                    borderRadius: '12px',
                    border: '1px solid #f0f0f0',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                    height: '100%'
                  }}
                  bodyStyle={{ padding: '20px' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{
                      background: '#ff4f2e',
                      borderRadius: '50%',
                      width: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '12px',
                      flexShrink: 0
                    }}>
                      <CheckOutlined style={{ color: 'white', fontSize: '16px' }} />
                    </div>
                    <Text strong style={{ fontSize: '14px' }}>{feature}</Text>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        </section>

        {/* FAQ Section */}
        <section style={{ padding: '60px 0', maxWidth: '900px', margin: '0 auto' }}>
          <Title level={2} style={{ color: '#0d306d', textAlign: 'center', marginBottom: '40px', fontSize: '2rem' }}>
            Frequently Asked Questions
          </Title>
          
          <Card style={{ borderRadius: '12px', marginBottom: '20px', border: '1px solid #f0f0f0' }}>
            <Title level={4} style={{ color: '#0d306d', marginBottom: '12px' }}>Is there a free trial available?</Title>
            <Paragraph style={{ margin: 0, color: '#666' }}>
              Yes, all plans include a 14-day free trial with full access to all features. No credit card required.
            </Paragraph>
          </Card>

          <Card style={{ borderRadius: '12px', marginBottom: '20px', border: '1px solid #f0f0f0' }}>
            <Title level={4} style={{ color: '#0d306d', marginBottom: '12px' }}>Can I change plans later?</Title>
            <Paragraph style={{ margin: 0, color: '#666' }}>
              Absolutely! You can upgrade or downgrade your plan at any time. Changes will be prorated based on your billing cycle.
            </Paragraph>
          </Card>

          <Card style={{ borderRadius: '12px', border: '1px solid #f0f0f0' }}>
            <Title level={4} style={{ color: '#0d306d', marginBottom: '12px' }}>Do you offer discounts for non-profits?</Title>
            <Paragraph style={{ margin: 0, color: '#666' }}>
              Yes, we offer special pricing for registered non-profit organizations. Please contact our sales team for more information.
            </Paragraph>
          </Card>
        </section>

        {/* CTA Section */}
        <section style={{ 
          padding: '60px 40px', 
          textAlign: 'center',
          background: 'linear-gradient(135deg, #0d306d 0%, #1a4580 100%)',
          borderRadius: '16px',
          marginBottom: '60px',
          color: 'white'
        }}>
          <Title level={2} style={{ color: 'white', marginBottom: '16px', fontSize: '2rem' }}>
            Still have questions?
          </Title>
          <Paragraph style={{ maxWidth: '600px', margin: '0 auto 24px', fontSize: '1.1rem', color: 'rgba(255, 255, 255, 0.9)' }}>
            Our team is ready to help you choose the right plan for your business needs.
          </Paragraph>
          <Space>
            <Button 
              type="primary" 
              size="large"
              onClick={handleContactSales}
              style={{ 
                background: '#ff4f2e',
                borderColor: '#ff4f2e',
                fontWeight: '600',
                fontSize: '16px',
                height: '48px',
                padding: '0 32px'
              }}
            >
              Contact Sales
            </Button>
          </Space>
        </section>
      </Content>

      {/* Contact Sales Modal */}
      <Modal
        title={
          <div style={{ textAlign: 'center' }}>
            <Title level={3} style={{ color: '#0d306d', margin: 0 }}>
              Contact Our Sales Team
            </Title>
            <Paragraph style={{ color: '#666', margin: 0 }}>
              We're here to help you choose the right solution
            </Paragraph>
          </div>
        }
        open={contactModalVisible}
        onCancel={() => setContactModalVisible(false)}
        footer={null}
        width={600}
        centered
      >
        <List
          itemLayout="horizontal"
          dataSource={salesContacts}
          renderItem={(item) => (
            <List.Item
              actions={[
                <Button 
                  type="primary" 
                  size="small"
                  onClick={() => handleContactAction(item.action)}
                  style={{ background: '#ff4f2e', borderColor: '#ff4f2e' }}
                >
                  Contact
                </Button>
              ]}
              style={{ padding: '16px 0', border: 'none' }}
            >
              <List.Item.Meta
                avatar={
                  <Avatar 
                    size="large" 
                    style={{ 
                      backgroundColor: '#0d306d',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {item.icon}
                  </Avatar>
                }
                title={<Text strong>{item.title}</Text>}
                description={
                  <div>
                    <div>{item.description}</div>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {item.details}
                    </Text>
                  </div>
                }
              />
            </List.Item>
          )}
        />
        <Divider />
        <div style={{ textAlign: 'center', padding: '16px 0' }}>
          <Text type="secondary">
            Business Hours: Monday - Friday, 8:00 AM - 6:00 PM EAT
          </Text>
        </div>
      </Modal>

      <style jsx>{`
        .pricing-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 32px rgba(0, 0, 0, 0.15) !important;
        }
        
        .pricing-button:hover {
          transform: translateY(-1px);
          box-shadow: 0 6px 16px rgba(13, 48, 109, 0.3) !important;
        }
      `}</style>
    </Layout>
  );
};

export default PricingPage;